import { Eye, Edit } from "lucide-react";
import { MobileCardsSkeleton } from "@/components/shared/mobile-cards-skeleton";
import type { InventoryItem } from "../modals/types";
import { getStatusColor, getLegendColor } from "../modals/types";

interface InventoryMobileCardsProps {
  items: InventoryItem[];
  loading: boolean;
  error: string | null;
  onViewItem: (item: InventoryItem) => void;
  onEditItem: (item: InventoryItem) => void;
  onRetry: () => void;
  searchQuery: string;
}

export function InventoryMobileCards({
  items,
  loading,
  error,
  onViewItem,
  onEditItem,
  onRetry,
  searchQuery,
}: InventoryMobileCardsProps) {
  if (loading) {
    return <MobileCardsSkeleton count={6} showHeader={false} />;
  }

  if (error) {
    return (
      <div className="text-center py-32">
        <div className="flex flex-col items-center justify-center gap-4">
          <p className="text-red-500 text-sm">{error}</p>
          <button
            onClick={onRetry}
            className="mt-4 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-32">
        <p className="text-muted-foreground text-sm">
          {searchQuery
            ? "No items match your search"
            : "No inventory items found"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          key={item.id}
          className="p-4 rounded-lg border bg-card border-border transition-colors"
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <p className="font-semibold">{item.item_name}</p>
              <p
                className="text-xs font-mono text-muted-foreground"
              >
                {item.item_code}
              </p>
              {item.category && (
                <p
                  className="text-xs mt-1 text-muted-foreground"
                >
                  {item.category}
                </p>
              )}
            </div>
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold ${getLegendColor(
                item.legend
              )}`}
            >
              {item.legend.replace("_", " ")}
            </span>
          </div>

          {/* Stock Info */}
          <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
            <div
              className="p-2 rounded bg-muted"
            >
              <span
                className={
                  "text-muted-foreground"
                }
              >
                Total Stock
              </span>
              <p
                className={`font-bold text-lg ${
                  item.stock === 0
                    ? "text-red-500"
                    : ""
                }`}
              >
                {item.stock}
              </p>
            </div>
            <div
              className="p-2 rounded bg-muted"
            >
              <span
                className={
                  "text-muted-foreground"
                }
              >
                Committed
              </span>
              <p className="font-bold text-lg text-orange-500">{item.committed_stock}</p>
            </div>
            <div
              className="p-2 rounded bg-muted"
            >
              <span
                className={
                  "text-muted-foreground"
                }
              >
                Available
              </span>
              <p
                className={`font-bold text-lg ${
                  item.available_stock === 0
                    ? "text-red-500"
                    : "text-green-500"
                }`}
              >
                {item.available_stock}
              </p>
            </div>
          </div>

          {/* Status */}
          <div className="mb-3">
            <span
              className={
                "text-muted-foreground text-xs"
              }
            >
              Status
            </span>
            <div className="mt-1">
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(
                  item.stock_status
                )}`}
              >
                {item.stock_status}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => onViewItem(item)}
              className="flex-1 px-3 py-2 rounded flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground transition-colors font-semibold text-sm"
            >
              <Eye className="h-4 w-4" />
              View
            </button>
            <button
              onClick={() => onEditItem(item)}
              className="flex-1 px-3 py-2 rounded flex items-center justify-center gap-2 bg-muted hover:bg-accent text-foreground transition-colors font-semibold text-sm"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
